'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { createGlobe } from './globe/createGlobe';

const PIN_COUNT = 3;

/** One L-shaped corner bracket of the pin card (10 × 1.5px bars, 1px outside the card). */
const CORNERS = [
  'left-[-0.205rem] top-[-0.205rem] border-l-[0.15375rem] border-t-[0.15375rem] lg:left-[-0.25rem] lg:top-[-0.25rem] lg:border-l-[0.1875rem] lg:border-t-[0.1875rem]',
  'right-[-0.205rem] top-[-0.205rem] border-r-[0.15375rem] border-t-[0.15375rem] lg:right-[-0.25rem] lg:top-[-0.25rem] lg:border-r-[0.1875rem] lg:border-t-[0.1875rem]',
  'bottom-[-0.205rem] left-[-0.205rem] border-b-[0.15375rem] border-l-[0.15375rem] lg:bottom-[-0.25rem] lg:left-[-0.25rem] lg:border-b-[0.1875rem] lg:border-l-[0.1875rem]',
  'bottom-[-0.205rem] right-[-0.205rem] border-b-[0.15375rem] border-r-[0.15375rem] lg:bottom-[-0.25rem] lg:right-[-0.25rem] lg:border-b-[0.1875rem] lg:border-r-[0.1875rem]',
];

function Pin({ pinRef }: { pinRef: (el: HTMLDivElement | null) => void }) {
  return (
    <div ref={pinRef} className="group absolute left-0 top-0 opacity-0 will-change-transform">
      {/* pulse ring: 80px in Figma (the pulse at full size); animated from the pin outwards */}
      <span
        data-pulse
        className="absolute left-[-2.93125rem] top-[-2.93125rem] h-[5.8625rem] w-[5.8625rem] rounded-full border border-[rgba(81,166,103,0.7)] opacity-50 lg:left-[-5rem] lg:top-[-5rem] lg:h-[10rem] lg:w-[10rem]"
      />
      <span className="absolute left-[-1.5375rem] top-[-1.5375rem] flex h-[3.075rem] w-[3.075rem] items-center justify-center rounded-full bg-[#51A667] lg:left-[-2.625rem] lg:top-[-2.625rem] lg:h-[5.25rem] lg:w-[5.25rem]">
        <svg viewBox="0 0 19 19" fill="none" className="h-[1.3875rem] w-[1.3875rem] lg:h-[2.375rem] lg:w-[2.375rem]" aria-hidden="true">
          <path d="M9.5 9.7375C11.2052 9.7375 12.5875 8.35518 12.5875 6.65C12.5875 4.94482 11.2052 3.5625 9.5 3.5625C7.79482 3.5625 6.4125 4.94482 6.4125 6.65C6.4125 8.35518 7.79482 9.7375 9.5 9.7375Z" stroke="white" strokeWidth="1.6625" />
          <path d="M3.8 15.9125C4.5125 13.0625 6.76875 11.4 9.5 11.4C12.2313 11.4 14.4875 13.0625 15.2 15.9125" stroke="white" strokeWidth="1.6625" />
        </svg>
      </span>
      <div
        className={cn(
          'absolute left-[3.4875rem] top-0 flex -translate-y-1/2 flex-col gap-[0.5125rem] whitespace-nowrap border-[0.1025rem] border-[rgba(81,166,103,0.28)] bg-[rgba(0,0,0,0.96)] pb-[1.325rem] pl-[1.6375rem] pr-[1.6375rem] pt-[1.225rem] backdrop-blur-[0.625rem]',
          'lg:left-[4.25rem] lg:gap-[0.625rem] lg:border-[0.125rem] lg:bg-black lg:pb-[1.625rem] lg:pl-[2rem] lg:pr-[2rem] lg:pt-[1.5rem]',
          // opens to the left near the right edge of the hero
          'group-data-[side=left]:left-auto group-data-[side=left]:right-[3.4875rem] lg:group-data-[side=left]:right-[4.25rem]',
          'opacity-0 transition-opacity [transition-delay:250ms] duration-500 group-data-[on=on]:opacity-100 motion-reduce:transition-none',
        )}
      >
        {CORNERS.map((c) => (
          <span key={c} className={cn('absolute h-[1.025rem] w-[1.025rem] border-[#9ee3b0] lg:h-[1.25rem] lg:w-[1.25rem]', c)} />
        ))}
        <span data-city className="block font-geist text-[1.5rem] font-medium leading-[2.05rem] tracking-[-0.005em] text-[#9ee3b0] lg:text-[2rem] lg:leading-[2.5rem]" />
        <span data-coords className="block whitespace-pre font-geist text-[1.25rem] font-medium leading-[1.9475rem] text-[rgba(255,255,255,0.66)] lg:text-[1.75rem] lg:leading-[2.375rem]" />
      </div>
    </div>
  );
}

export default function EliteHeroGlobe({ className }: { className?: string }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const arcsRef = useRef<HTMLCanvasElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const pinRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const root = rootRef.current, box = boxRef.current, arcs = arcsRef.current;
    if (!root || !box || !arcs) return;
    const destroy = createGlobe({
      root,
      box,
      arcs,
      pins: pinRefs.current.filter((el): el is HTMLDivElement => !!el),
      label: labelRef.current,
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      onReady: () => setReady(true),
      onLost: () => setReady(false),
    });
    return () => {
      destroy?.();
      setReady(false);
    };
  }, []);

  return (
    <div ref={rootRef} aria-hidden="true" className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}>
      {/* Figma: desktop 1244×490 centred, 46px below the hero bottom; mobile 503.8×198.4 on the bottom */}
      <div
        ref={boxRef}
        className={cn(
          // Centred with a calculated `left`, not mx-auto (which pins a box wider than the screen to the
          // left edge) and not a translate (createGlobe reads offsetLeft, which ignores transforms).
          'pointer-events-auto absolute bottom-0 left-[calc(50%-31.4875rem)] h-[24.8rem] w-[62.975rem] lg:bottom-[-5.75rem] lg:left-[calc(50%-77.75rem)] lg:h-[61.25rem] lg:w-[155.5rem]',
          '[&>canvas]:transition-opacity [&>canvas]:duration-700 motion-reduce:[&>canvas]:transition-none',
          ready ? '[&>canvas]:opacity-100' : '[&>canvas]:opacity-0',
        )}
      >
        <img
          src="/elite/globe-fallback.png"
          alt=""
          width={2488}
          height={980}
          decoding="async"
          draggable={false}
          className={cn(
            'pointer-events-none absolute inset-0 h-full w-full select-none transition-opacity duration-700 motion-reduce:transition-none',
            ready ? 'opacity-0' : 'opacity-100',
          )}
        />
      </div>
      <canvas ref={arcsRef} className="absolute inset-0 h-full w-full" />
      <div className="absolute inset-0">
        {Array.from({ length: PIN_COUNT }, (_, i) => (
          <Pin key={i} pinRef={(el) => { pinRefs.current[i] = el; }} />
        ))}
      </div>
      {/* country name pill that follows the mouse over the globe (desktop only) */}
      <div
        ref={labelRef}
        className={cn(
          'absolute left-0 top-0 z-[3] hidden h-[4.25rem] items-center gap-[1rem] whitespace-nowrap rounded-full border border-black/[0.06] bg-white/[0.92] pl-[1.375rem] pr-[1.75rem] font-geist text-[1.75rem] font-medium text-[#0b0b0b] lg:flex',
          'shadow-[0_0.25rem_0.5rem_rgba(0,0,0,0.04),0_1.5rem_3.5rem_-1.5rem_rgba(0,0,0,0.18)] backdrop-blur-[1.25rem]',
          'opacity-0 blur-[4px] [transform:translate(2rem,-120%)_scale(0.92)]',
          'transition-[opacity,transform,filter] [transition-duration:350ms] [transition-timing-function:cubic-bezier(.22,.61,.36,1)]',
          'data-[on=on]:opacity-100 data-[on=on]:blur-0 data-[on=on]:[transform:translate(2rem,-120%)_scale(1)]',
        )}
      >
        <i className="h-[0.875rem] w-[0.875rem] rounded-full bg-[#51A667] shadow-[0_0_0_0.4375rem_rgba(81,166,103,0.2)]" />
        <span data-name />
      </div>
    </div>
  );
}
